import { useCallback, useEffect } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { notification } from 'antd'
import { Message } from "../backend/types/notification.ts"
import useWebSocket from "react-use-websocket"
import { useNavigate } from "react-router-dom"

const websocketUrl = import.meta.env.VITE_WS_URL || `wss://${window.location.host}/`

export const useQueryInvalidationSubscription = () => {
    const queryClient = useQueryClient()
    const navigate = useNavigate()
    const { lastMessage } = useWebSocket(websocketUrl, {
        share: true,
        shouldReconnect: () => true,
        reconnectAttempts: 10,
        heartbeat: {
            message: JSON.stringify({
                type: 'ping'
            }),
            returnMessage: JSON.stringify({
                type: 'pong',
            }),
            timeout: 60000,
            interval: 30000,
        }
    })
    const [api, contextHolder] = notification.useNotification()

    const openSuccessNotification = useCallback((message: string, description: string = '') => {
        api.success({
            message,
            description: <span dangerouslySetInnerHTML={{ __html: description }}></span>,
            placement: 'topRight',
            duration: 5
        })
    }, [api])

    const openFailureNotification = useCallback((message: string) => {
        api.error({
            message,
            placement: 'topRight',
        })
    }, [api])

    const openInfoNotification = useCallback((message: string) => {
        api.info({
            message,
            placement: 'topRight',
        })
    }, [api])

    useEffect(() => {
        if (!lastMessage) return
        const data = JSON.parse(lastMessage.data) as Message
        switch (data.type) {
        case 'invalidation':
            (data.queryKeys as string[])?.forEach((queryKey: string) => {
                queryClient.invalidateQueries({
                    predicate: (query) => (
                        Array.isArray(query.queryKey) ?
                            query.queryKey.some((keyPart) => keyPart?.toString().includes(queryKey)) :
                            query.queryKey.toString().includes(queryKey)
                    )
                })
            })
            break
        case 'success':
            openSuccessNotification(data.message, data.description)
            break
        case 'error':
            openFailureNotification(data.message)
            break
        case 'info':
            openInfoNotification(data.message)
            break
        case "version":
            navigate(`/moneyflow/${data.message}`)
        }

    }, [openSuccessNotification, openFailureNotification, queryClient, openInfoNotification, lastMessage, navigate])

    return ({ contextHolder })
}
